import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import StatusBadge from '../../components/doorlypro/StatusBadge';
import BorrowerConsentCell from '../../components/doorlypro/BorrowerConsentCell';
import { getStatusMeta } from '../../constants/partnerStatuses';
import WithdrawFileControl from '../../components/doorlypro/WithdrawFileControl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// MY BORROWERS — AN ORIGINATOR'S OWN PIPELINE, WHICH DID NOT EXIST.
//
// 🚨 A realtor has My Buyers, reading buyer_referrals. An originator's files
// live in broker_submissions and the only place they appeared was the bottom of
// the submission form — so an LO could see what they had SENT and not what had
// become of it. No statuses, no pipeline, nothing answering "where is my
// borrower".
//
// 🚨 AND THE DASHBOARD'S QUICK ACTION SENT THEM TO THE REALTOR PAGE, which
// offers "Submit a Buyer" — a referral an originator must not file.
// referralRefusalForType refuses it, and the nav's own comment says why: an LO
// who filed one would be owed the ORIGINATION rate on the loan amount for an
// introduction with no origination in it. A wrong door with a payment behind it.
//
// 🔑 ONE STATUS VOCABULARY. The labels come from the same getPartnerStatus the
// buyers list uses, rendered through the same getStatusMeta, so the two pages
// cannot describe the same stage differently.
//
// ⚠️ IT DISCLOSES NOTHING NEW. Every field is one the originator sent us
// themselves, plus the status label — no approval amount, no shopping range.
// ─────────────────────────────────────────────────────────────────────────────


// 🔑 BEHIND THE SIGN-IN CHECK, LIKE EVERY OTHER DoorlyPro PAGE (broker report 1102, Jesse 2026-11-02). This page was
// routed bare: a signed-out visitor got the partner shell and its requests went out with a null token (each refused by
// the server — verifyPartnerToken — but the page itself never sent them to sign in). RequirePartnerAuth holds the render
// until the session is read and sends a signed-out visitor to /doorlypro/login; the page's own hooks run only after.
export default function DoorlyProBorrowers() {
  return (
    <RequirePartnerAuth>
      <DoorlyProBorrowersPage />
    </RequirePartnerAuth>
  );
}

function DoorlyProBorrowersPage() {
  const navigate = useNavigate();
  const { token } = useDoorlyProAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  // 🔑 EXTRACTED SO A RESEND CAN ASK FOR IT AGAIN. The row's "Invite sent
  // <date>" comes from this payload, so after a successful resend the list has
  // to be reloaded or the date stays stale while the cell says it just sent.
  const load = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/doorlypro/borrowers`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else setData(d); })
      // ⚠️ A FAILED REFRESH MUST NOT WIPE A GOOD LIST. setError leaves `data`
      // alone, so the rows on screen stay rather than blanking.
      .catch(() => setError('Could not load your borrowers.'));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const rows = (data && data.borrowers) || [];

  return (
    <DoorlyProLayout>
      {/* ⚠️ NO INNER maxWidth. It was capped at 900px, which left the table
          squeezed into two thirds of the page while Team Pipeline — the same
          shape, the same columns — fills the content area. The layout already
          owns the gutters. */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>
            My Borrowers
          </h1>
          {/* 🔑 "Submit a Borrower" GOES TO THE APPLICATION FORM, not to the
              realtor referral form. That mis-routing is the bug this page was
              built out of. */}
          <button type="button" onClick={() => navigate('/doorlypro/submissions')}
            style={{ flexShrink: 0, padding: '9px 16px', background: '#3a6ad9', color: '#fff',
              border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer' }}>
            + Submit a Borrower
          </button>
        </div>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 14px' }}>
          Every loan file you have sent Doorly, and what each one is waiting on.
        </p>
      </div>

      {error && (
        <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 16 }}>{error}</div>
      )}

      {/* ⚠️ A REALTOR WHO REACHES THIS URL IS TOLD IT IS THE WRONG PAGE rather
          than shown an empty table, which they would read as "I have no
          buyers". The server answers not_for_type; the page does not guess from
          emptiness. */}
      {data?.not_for_type ? (
        <div style={{ background: '#f5f8ff', border: '1px solid #c5d3f5', borderRadius: 10,
          padding: '14px 16px', fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>
          {data.message}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              {/* 🚨 THE HEADER STAYS WHEN THERE ARE NO ROWS. An empty card with
                  a sentence in it says "nothing here"; the header says what WILL
                  be here, so somebody deciding whether to send their first file
                  can see what they will get back. */}
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Borrower', 'Status', 'What it\u2019s waiting on', 'Consent', 'Submitted'].map((col) => (
                    <th key={col} style={{ padding: '10px 16px', textAlign: 'left',
                      fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '34px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827',
                        marginBottom: 6 }}>
                        {data ? 'No loan files yet' : 'Loading\u2026'}
                      </div>
                      {data && (
                        <>
                          <div style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.7,
                            maxWidth: 520, margin: '0 auto 16px' }}>
                            Send a file as soon as you have taken the application. You do not need
                            a property address and you do not need to wait for your borrower to
                            find a house.
                          </div>
                          <button type="button"
                            onClick={() => navigate('/doorlypro/submissions')}
                            style={{ padding: '9px 16px', background: '#3a6ad9', color: '#fff',
                              border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 13.5,
                              cursor: 'pointer' }}>
                            Submit a Borrower
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : rows.map((b, idx) => {
                  const meta = getStatusMeta(b.partner_status, 'loan_officer');
                  return (
                    <tr key={b.id} style={{ borderBottom: idx < rows.length - 1
                      ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '12px 16px', color: '#111827', fontWeight: 600 }}>
                        {b.name || b.email || `File #${b.id}`}
                        {b.email && b.name && (
                          <div style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12,
                            marginTop: 2 }}>{b.email}</div>
                        )}
                        {/* 🚨 WHETHER THE BORROWER HAS CLAIMED THE FILE, because
                            it is the one thing the originator can act on — an
                            unclaimed file is waiting on a person, not on
                            Doorly, and nothing on the old submission list said
                            so. */}
                        {!b.registered && (
                          <div style={{ fontWeight: 600, color: '#92400e', fontSize: 11.5,
                            marginTop: 4 }}>
                            Has not registered with Doorly yet
                          </div>
                        )}
                        {/* 🔑 WITHDRAW, while the borrower has not registered (broker gaps, 2026-11-02). Shown only when
                            the server's switch is on (withdraw_enabled, on by default since 2026-11-03; 'off' is the kill switch). */}
                        {data?.withdraw_enabled && !b.registered
                          && b.partner_status !== 'Withdrawn' && b.partner_status !== 'Expired' && (
                          <div style={{ marginTop: 6 }}>
                            <WithdrawFileControl token={token} submissionId={b.id}
                              borrowerName={b.name || b.email} onWithdrawn={load} />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <StatusBadge status={b.partner_status} partnerType="loan_officer" />
                      </td>
                      {/* ⚠️ THE SAME nextStep THE JOURNEY AND EVA READ, resolved
                          for an ORIGINATOR — the overrides exist so this does
                          not say "waiting on the buyer" to somebody whose client
                          is a borrower. */}
                      <td style={{ padding: '12px 16px', color: '#6b7280', maxWidth: 420 }}>
                        {meta.nextStep || '\u2014'}
                      </td>
                      {/* 🔑 "Submitted", NOT "Sent" — broker_submissions.created_at,
                          the moment the file passed validation and landed here.
                          "Sent" could be read as sent to the BORROWER, which is
                          a different event with a different date. The row is
                          only inserted on a real submission (there are no draft
                          rows) and nothing updates created_at afterwards, so
                          the header is a claim the column can keep. */}
                      {/* 🚨 WHETHER DOORLY MAY TOUCH THIS FILE'S CREDIT, on the
                          page an originator actually lives on. The request used
                          to exist only on the panel shown seconds after
                          submitting, so a file could sit here for weeks with no
                          authorization and nothing saying so. */}
                      <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                        <BorrowerConsentCell token={token} submissionId={b.id}
                          consent={b.consent} registered={b.registered}
                          invitedAt={b.invited_at} borrowerName={b.name || b.email}
                          withdrawn={b.partner_status === 'Withdrawn'}
                          onResent={load} />
                      </td>
                      <td style={{ padding: '12px 16px', color: '#9ca3af',
                        whiteSpace: 'nowrap' }}>
                        {b.created_at ? new Date(b.created_at).toLocaleDateString() : '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DoorlyProLayout>
  );
}
